import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, Download, Share2 } from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  isSameMonth, isToday, format,
} from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { COVERAGE_TEAM, shortName } from "../MultiPersonSelect";

// Un color fijo por persona (petición usuario 2026-08-27: "colores segun la
// persona") + gris para "Sin asignar" — mismo orden que COVERAGE_TEAM.
const PERSON_COLORS = ["#2563eb", "#16a34a", "#d97706", "#db2777", "#7c3aed", "#0891b2"];
const UNASSIGNED_COLOR = "#94a3b8";
const UNASSIGNED_KEY = "__unassigned__";

export function personColor(name) {
  const idx = COVERAGE_TEAM.indexOf(name);
  return idx >= 0 ? PERSON_COLORS[idx] : UNASSIGNED_COLOR;
}

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function icsEscape(s) {
  return String(s || "").replace(/[\\;,]/g, (m) => `\\${m}`).replace(/\n/g, "\\n");
}

function toICSDate(d) {
  return format(d, "yyyyMMdd");
}

function toICSDateTime(dateStr, timeStr) {
  const [h, m] = (timeStr || "00:00").split(":");
  const d = new Date(dateStr);
  d.setHours(Number(h) || 0, Number(m) || 0, 0, 0);
  return format(d, "yyyyMMdd'T'HHmmss");
}

// Fix 2026-08-27: exportar .ics respetando el filtro de personas activo, para
// que cada persona pueda llevarse "su" calendario a Outlook.
function downloadICS(eventos) {
  const now = toICSDate(new Date()) + "T000000Z";
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Valoriza Innovation OS//Eventos Innovacion//ES", "CALSCALE:GREGORIAN"];
  eventos.forEach((e) => {
    if (!e.date) return;
    const start = new Date(e.date);
    const endBase = e.end_date ? new Date(e.end_date) : start;
    lines.push("BEGIN:VEVENT");
    lines.push(`UID:${e.id}@valoriza-innovation-os`);
    lines.push(`DTSTAMP:${now}`);
    if (e.start_time) {
      lines.push(`DTSTART:${toICSDateTime(e.date, e.start_time)}`);
      lines.push(`DTEND:${toICSDateTime(e.end_date || e.date, e.end_time || e.start_time)}`);
    } else {
      const dtEnd = addDays(endBase, 1); // DTEND exclusivo para eventos de día completo
      lines.push(`DTSTART;VALUE=DATE:${toICSDate(start)}`);
      lines.push(`DTEND;VALUE=DATE:${toICSDate(dtEnd)}`);
    }
    lines.push(`SUMMARY:${icsEscape(e.name)}`);
    if (e.description) lines.push(`DESCRIPTION:${icsEscape(e.description)}`);
    if (e.location) lines.push(`LOCATION:${icsEscape(e.location)}`);
    lines.push("END:VEVENT");
  });
  lines.push("END:VCALENDAR");
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "eventos-innovacion.ics";
  a.click();
  URL.revokeObjectURL(url);
}

function ownersOf(e) {
  const cb = e.covered_by;
  if (Array.isArray(cb)) return cb;
  return cb ? [cb] : [];
}

function DayCell({ day, inMonth, dayEvents, tall, onEventClick }) {
  const key = format(day, "yyyy-MM-dd");
  return (
    <div
      className={`bg-background p-1.5 flex flex-col gap-1 ${tall ? "min-h-[220px]" : "min-h-[104px]"} ${inMonth ? "" : "opacity-40"}`}
      data-testid={`calendar-day-${key}`}
    >
      <span className={`text-xs font-medium ${isToday(day) ? "inline-flex w-5 h-5 items-center justify-center rounded-full bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
        {format(day, "d")}
      </span>
      <div className={`flex-1 space-y-1 ${tall ? "overflow-y-auto" : "overflow-hidden"}`}>
        {dayEvents.slice(0, tall ? 20 : 3).map((e) => {
          const owners = ownersOf(e);
          const color = owners.length > 0 ? personColor(owners[0]) : UNASSIGNED_COLOR;
          return (
            <button
              key={e.id}
              onClick={() => onEventClick(e)}
              title={e.name}
              className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate text-white hover:opacity-80 transition-opacity"
              style={{ backgroundColor: color }}
              data-testid={`calendar-event-${e.id}`}
            >
              {e.start_time && <span className="opacity-80">{e.start_time} </span>}{e.name}
            </button>
          );
        })}
        {!tall && dayEvents.length > 3 && (
          <span className="text-[10px] text-muted-foreground px-1.5">+{dayEvents.length - 3} más</span>
        )}
      </div>
    </div>
  );
}

export default function EventosCalendarTab({ eventos, onEventClick }) {
  const [cursor, setCursor] = useState(new Date());
  const [mode, setMode] = useState("month"); // "month" | "week" | "day"
  const [excluded, setExcluded] = useState(() => new Set());

  const shareCalendarLink = () => {
    const url = `${window.location.origin}/embed/eventos-calendario`;
    navigator.clipboard?.writeText(url);
    toast.success("Link copiado — vista de solo lectura, sin necesidad de iniciar sesión");
  };

  const togglePerson = (key) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const filteredEventos = useMemo(() => {
    if (excluded.size === 0) return eventos;
    return eventos.filter((e) => {
      const owners = ownersOf(e);
      const keys = owners.length > 0 ? owners : [UNASSIGNED_KEY];
      return keys.some((o) => !excluded.has(o));
    });
  }, [eventos, excluded]);

  const days = useMemo(() => {
    if (mode === "day") return [cursor];
    const start = mode === "week"
      ? startOfWeek(cursor, { weekStartsOn: 1 })
      : startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = mode === "week"
      ? endOfWeek(cursor, { weekStartsOn: 1 })
      : endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const out = [];
    let d = start;
    while (d <= end) { out.push(d); d = addDays(d, 1); }
    return out;
  }, [cursor, mode]);

  const byDay = useMemo(() => {
    const map = {};
    for (const e of filteredEventos) {
      if (!e.date) continue;
      const key = format(new Date(e.date), "yyyy-MM-dd");
      (map[key] = map[key] || []).push(e);
    }
    return map;
  }, [filteredEventos]);

  const peopleInView = COVERAGE_TEAM.filter((name) =>
    eventos.some((e) => ownersOf(e).includes(name))
  );

  const navigate = (dir) => setCursor(mode === "day" ? addDays(cursor, dir) : mode === "week" ? addWeeks(cursor, dir) : addMonths(cursor, dir));

  return (
    <div className="space-y-4" data-testid="eventos-calendar-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-primary" />
          {mode === "day"
            ? format(cursor, "EEEE d 'de' MMMM yyyy", { locale: es })
            : mode === "week"
            ? `Semana del ${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM", { locale: es })}`
            : format(cursor, "MMMM yyyy", { locale: es })}
        </h2>
        <div className="flex gap-2 items-center">
          <div className="flex rounded-md border overflow-hidden">
            <button
              onClick={() => setMode("month")}
              className={`text-xs px-3 py-1.5 ${mode === "month" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid="calendar-view-month-btn"
            >Mes</button>
            <button
              onClick={() => setMode("week")}
              className={`text-xs px-3 py-1.5 ${mode === "week" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid="calendar-view-week-btn"
            >Semana</button>
            <button
              onClick={() => setMode("day")}
              className={`text-xs px-3 py-1.5 ${mode === "day" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid="calendar-view-day-btn"
            >Día</button>
          </div>
          <Button size="icon" variant="outline" onClick={() => navigate(-1)} data-testid="calendar-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date())} data-testid="calendar-today-btn">Hoy</Button>
          <Button size="icon" variant="outline" onClick={() => navigate(1)} data-testid="calendar-next-month"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={shareCalendarLink} data-testid="calendar-share-btn">
            <Share2 className="w-3.5 h-3.5 mr-1.5" />Compartir
          </Button>
          <Button size="sm" variant="outline" onClick={() => downloadICS(filteredEventos)} data-testid="calendar-export-ics-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />Exportar .ics
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="calendar-legend">
        {peopleInView.map((name) => {
          const isExcluded = excluded.has(name);
          return (
            <button
              key={name}
              onClick={() => togglePerson(name)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full transition-opacity ${isExcluded ? "opacity-35" : "bg-muted"}`}
              data-testid={`calendar-legend-${name.replace(/\s+/g, "-")}`}
            >
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: personColor(name) }} />
              {shortName(name)}
            </button>
          );
        })}
        <button
          onClick={() => togglePerson(UNASSIGNED_KEY)}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-muted-foreground transition-opacity ${excluded.has(UNASSIGNED_KEY) ? "opacity-35" : "bg-muted"}`}
          data-testid="calendar-legend-sin-asignar"
        >
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: UNASSIGNED_COLOR }} />
          Sin asignar
        </button>
        {excluded.size > 0 && (
          <button onClick={() => setExcluded(new Set())} className="text-xs underline text-muted-foreground hover:text-foreground" data-testid="calendar-legend-reset">
            Ver todos
          </button>
        )}
      </div>

      {mode === "day" ? (
        <div className="rounded-lg border bg-background p-4 space-y-2 min-h-[300px]" data-testid="calendar-day-view">
          {(byDay[format(cursor, "yyyy-MM-dd")] || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">Sin eventos este día.</p>
          ) : (
            (byDay[format(cursor, "yyyy-MM-dd")] || []).map((e) => {
              const owners = ownersOf(e);
              const color = owners.length > 0 ? personColor(owners[0]) : UNASSIGNED_COLOR;
              return (
                <button
                  key={e.id}
                  onClick={() => onEventClick(e)}
                  className="w-full flex items-start gap-3 text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                  data-testid={`calendar-event-${e.id}`}
                >
                  <span className="w-1.5 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{e.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {e.start_time ? `${e.start_time}${e.end_time ? ` - ${e.end_time}` : ""} · ` : ""}
                      {e.location || "Ubicación por confirmar"} {owners.length > 0 ? `· ${owners.map(shortName).join(", ")}` : "· Sin asignar"}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border">
          {WEEKDAYS.map((w) => (
            <div key={w} className="bg-muted text-center text-xs font-medium text-muted-foreground py-2">{w}</div>
          ))}
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            return (
              <DayCell
                key={key}
                day={d}
                inMonth={mode === "week" ? true : isSameMonth(d, cursor)}
                dayEvents={byDay[key] || []}
                tall={mode === "week"}
                onEventClick={onEventClick}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
